import classes from "./PageHeader.module.css"
import {IRoute, RouteSelector} from "./RouteSelector";
import {useLocation, useNavigate} from "react-router";
import {
    Box,
    Button,
    Image,
    Menu,
    Text,
    useCombobox,
} from "@mantine/core";
import {IconSettings} from "@tabler/icons-react";
import React, {useMemo, useState} from "react";
import {useNeurotuneStore} from "../store/neurotune";
import {v4 as uuidv4} from "uuid";
import {modals} from "@mantine/modals";
import {ICompany} from "../types";
import cx from "clsx";
import commonClasses from "../common.module.css";
import {useProtocolStore} from "../../protocols/store/protocols";
import {useDashboardStore} from "../../protocols/analysis/dashboards/store";
import {useProtocolDefinitionsStore} from "../../protocols/store/protocolDefinitions";
import {
    openCompanySelectionModal,
    openRegionSelectionModal,
    Region,
    Regions
} from "../modals";
import {getAuth, signOut} from "@firebase/auth";
import {invokeTauriCommand, isTauriAvailable} from "@/common/utils/tauri";

export interface PageHeaderProps {
    routes: Array<IRoute>
}

/**
 * Maps a frontend region to an NDC environment id. In the NDC webview the
 * region switcher routes through the app's `navigate` command so the
 * switch gets the loader overlay, window-title update, and forced-reload
 * semantics instead of a raw page navigation.
 */
export const regionToNdcEnv = (region: Region): "uk" | "test" | "dev" => {
    switch (region.name) {
        case "UK":
            return "uk";
        case "Test":
            return "test";
        default:
            return "dev";
    }
};


export const showCompanySelectionModal = (activeCompany: ICompany, companies: Array<ICompany>, onCompanySelected: (company: ICompany) => void) => {
    const modalId = uuidv4()
    const onSelect = (company: ICompany) => {
        onCompanySelected(company);
        modals.close(modalId)
    }

    return openCompanySelectionModal(modalId, activeCompany, companies, onSelect);
}

export const PageHeader: React.FC<PageHeaderProps> = ({routes}) => {
    const user = useNeurotuneStore(state => state.user)
    const resetProtocolDefsCache = useProtocolDefinitionsStore(state => state.reset);
    const resetProtocolCache = useProtocolStore(state => state.reset);
    const resetDashboardCache = useDashboardStore(state => state.reset);
    const activeCompany = useNeurotuneStore(state => state.activeCompany);
    const companies = useNeurotuneStore(state => state.companies);
    const setActiveCompany = useNeurotuneStore(state => state.setActiveCompany);
    const navigate = useNavigate()
    const location = useLocation()
    const [selectedRegion, setSelectedRegion] = useState<Region>(Object.values(Regions).find((r) => window.location.origin.startsWith(r.origin)) || Regions.Local);
    const regionCombobox = useCombobox({
        onDropdownClose: () => regionCombobox.resetSelectedOption(),
    });

    // we need to copy then sort the routes by length so that we test against most specific to lease specific routes.
    const activeRoute = useMemo(() => [...routes].sort((r1, r2) => r1.path.length > r2.path.length ? -1 : 1).find((r) => location.pathname.startsWith(r.path)) ||
            routes.find((r) => r.default)
        , [routes,location])

    const onRouteSelected = (route: IRoute) => {
        navigate(route.path)
    }

    const onRegionSelected = async (region: Region) => {
        if (region.origin !== window.location.origin) {
            if (isTauriAvailable()) {
                // NDC webview: route the switch through the app so it gets
                // the loader overlay, an interrupt of any in-flight load and
                // a window-title update. Falls back to a raw navigation if
                // the running NDC build doesn't support the command.
                try {
                    await invokeTauriCommand("navigate", {env: regionToNdcEnv(region)});
                    setSelectedRegion(region)
                    return
                } catch (e) {
                    console.warn("NDC navigate command failed, falling back to page navigation", e)
                }
            }
            window.location.href = region.origin
        }
        setSelectedRegion(region)
    }

    const renderRegionSelector = () => {
        return (
            <Box style={{
                display: "grid",
                alignItems: "center",
                grid: "auto / min-content min-content",
                columnGap: "0.25em"
            }}
                 onClick={() => {
                     const modalId = uuidv4();
                     return openRegionSelectionModal(modalId, selectedRegion, onRegionSelected, user?.email)
                 }
                 }
            >
                <Text size={"1em"}>{selectedRegion?.icon}</Text><Text>{selectedRegion?.name}</Text>
            </Box>
        )
    }
    const onCompanySelected = async (company: ICompany) => {
        // reset all our store caches and navigate to home
        resetProtocolDefsCache();
        resetProtocolCache();
        resetDashboardCache();
        setActiveCompany(company)
        navigate("/")
    }

    return (
        <div className={classes.root}>
            <div className={classes.content}>
                <Box style={{display: "grid", grid: "auto / 14em auto", alignItems: "center", columnGap: "1em"}}>
                    <RouteSelector className={classes.route} routes={routes} {...(activeRoute && {activeRoute})}
                                   onRouteSelected={onRouteSelected}/>
                    <Box style={{justifySelf: "start"}}>
                        <Image className={classes.logo} height={"30rem"}
                               src={"/assets/images/BIOS_Logo_White_RGB.png"}/>
                    </Box>
                </Box>
                <Box className={classes.companyName}>
                    <Button fw={"bold"} size={"1em"} variant={"unstyled"} color={"var(--bios-light-text)"}
                            onClick={() => {
                                showCompanySelectionModal(activeCompany, companies, onCompanySelected);
                            }}
                            className={cx(commonClasses.borderHover)}>{activeCompany?.name || "No Company Selected"}</Button>
                </Box>

                <Menu shadow="md" width={250} position="bottom-end">
                    <Menu.Target>
                        <Button
                            style={{justifySelf: "end"}}
                            ml={"1em"}
                        >
                            <IconSettings size="1rem"/>
                        </Button>
                    </Menu.Target>

                    <Menu.Dropdown>
                        <Menu.Label>Settings</Menu.Label>
                        <Menu.Item onClick={() => navigate("/settings/preferences")}>
                            Preferences
                        </Menu.Item>
                        <Menu.Item onClick={() => navigate("/settings/api-keys")}>
                            API Keys
                        </Menu.Item>
                        <Menu.Item onClick={() => navigate("/settings/software")}>
                            Software
                        </Menu.Item>
                        <Menu.Item onClick={() => navigate("/settings/about")}>
                            About
                        </Menu.Item>

                        <Menu.Divider/>
                        <Menu.Label>Region</Menu.Label>
                        <Menu.Item closeMenuOnClick={false}>
                            {renderRegionSelector()}
                        </Menu.Item>

                        <Menu.Divider/>
                        <Menu.Item closeMenuOnClick={false} style={{padding: 0}}>
                            <Box
                                style={{
                                    color: "var(--mantine-color-red-6)",
                                    cursor: "pointer",
                                    padding: "0.5em 1em",
                                    borderRadius: "var(--bios-border-radius)",
                                    border: "var(--bios-border)",
                                    textAlign: "center",
                                    fontWeight: 500,
                                    transition: "all 0.2s ease"
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    signOut(getAuth()).then(() => {
                                        // log out
                                        window.location.href = `${window.BIOS.neurotune_base_url}/logout?redirect_url=` + encodeURI(`${window.location.origin}?lm=${window.BIOS.loginMode}`);
                                    })
                                }}
                                className={cx(commonClasses.hover, commonClasses.dropshadowHover)}
                            >
                                Log Out
                            </Box>
                        </Menu.Item>
                    </Menu.Dropdown>
                </Menu>

            </div>
        </div>
    )
}